import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import { Add, Lock, LockOpen, Replay, Save } from "@material-ui/icons";
import { Autocomplete } from "@material-ui/lab";

import KPTable from "../../../components/KPTable";
import { apiCall } from "../../../utils/api";
import { API_URLS } from "../../../constants/api";
import { notification } from "../../../reducer/home/action";

// Admin tab for the research-hour subsystem:
//   • Section A — quota chung theo năm: 1 row per year, default 586. Editable
//     inline; FE never deletes a row (auto-seeded on first read by BE).
//   • Section B — reduction % theo người: filter by year, then upsert pct per
//     profile. Disabled when the year is closed (snapshot would go stale).
//   • Section C — "chốt năm" / "mở lại": status card + button. Closing freezes
//     the year's hours_done / surplus into research_hour_year_balances.
//
// All API calls use plain apiCall — no Redux state for these admin settings,
// they're rarely opened and don't need to be shared across screens.

const currentYear = new Date().getFullYear();
const yearOptions = (() => {
  const out = [];
  for (let y = currentYear + 1; y >= currentYear - 5; y -= 1) out.push(y);
  return out;
})();

function ResearchHourQuotaForm() {
  const dispatch = useDispatch();
  const [year, setYear] = useState(currentYear);

  // Section A: quota list
  const [quotas, setQuotas] = useState([]);
  const [quotaLoading, setQuotaLoading] = useState(false);
  const [edittingQuota, setEdittingQuota] = useState(null); // { year, hours }
  const [creatingQuota, setCreatingQuota] = useState(null); // { year, hours }

  // Section B: reductions for the selected year
  const [reductions, setReductions] = useState([]);
  const [reductionLoading, setReductionLoading] = useState(false);
  const [edittingReduction, setEdittingReduction] = useState(null);
  const [creatingReduction, setCreatingReduction] = useState(null);
  const [allProfiles, setAllProfiles] = useState([]);

  // Section C: year close status
  const [yearStatus, setYearStatus] = useState(null);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [confirmReopenOpen, setConfirmReopenOpen] = useState(false);

  // -- Loaders --------------------------------------------------------------
  const loadQuotas = async () => {
    setQuotaLoading(true);
    const { response } = await apiCall({ ...API_URLS.RESEARCH_HOUR_CONFIG.listYearQuotas() });
    if (response?.data?.success) setQuotas(response.data.data || []);
    setQuotaLoading(false);
  };

  const loadReductions = async (y) => {
    setReductionLoading(true);
    const { response } = await apiCall({ ...API_URLS.RESEARCH_HOUR_CONFIG.listReductions(y) });
    if (response?.data?.success) setReductions(response.data.data || []);
    setReductionLoading(false);
  };

  const loadYearStatus = async (y) => {
    const { response } = await apiCall({ ...API_URLS.RESEARCH_HOUR_CONFIG.yearStatus(y) });
    if (response?.data?.success) setYearStatus(response.data.data);
    else setYearStatus(null);
  };

  const loadAllProfiles = async () => {
    const { response } = await apiCall({ ...API_URLS.PROFILE.getAllProfiles() });
    if (response?.data?.success) setAllProfiles(response.data.data || []);
  };

  useEffect(() => {
    loadQuotas();
    loadAllProfiles();
  }, []);

  useEffect(() => {
    loadReductions(year);
    loadYearStatus(year);
  }, [year]);

  const yearClosed = !!yearStatus?.closed;

  // -- Quota actions --------------------------------------------------------
  const saveQuota = async (row) => {
    const v = parseInt(row.hours, 10);
    if (Number.isNaN(v) || v < 0) {
      dispatch(notification({ type: "error", message: "Số giờ phải là số nguyên >= 0" }));
      return;
    }
    const { response, error } = await apiCall({
      ...API_URLS.RESEARCH_HOUR_CONFIG.upsertYearQuota(),
      payload: { year: row.year, hours: v },
    });
    if (!error && response?.data?.success) {
      dispatch(notification({ type: "success", message: `Đã lưu quota năm ${row.year}` }));
      setEdittingQuota(null);
      setCreatingQuota(null);
      loadQuotas();
    } else {
      dispatch(notification({ type: "error", message: response?.data?.message || "Lưu thất bại" }));
    }
  };

  // -- Reduction actions ----------------------------------------------------
  const saveReduction = async (row) => {
    const pct = parseFloat(row.pct);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      dispatch(notification({ type: "error", message: "Reduction phải trong [0, 100]" }));
      return;
    }
    if (!row.profileId) {
      dispatch(notification({ type: "error", message: "Chưa chọn cán bộ" }));
      return;
    }
    const { response, error } = await apiCall({
      ...API_URLS.RESEARCH_HOUR_CONFIG.upsertReduction(),
      payload: { profileId: row.profileId, year, pct, note: row.note || "" },
    });
    if (!error && response?.data?.success) {
      dispatch(notification({ type: "success", message: "Đã lưu giảm trừ" }));
      setEdittingReduction(null);
      setCreatingReduction(null);
      loadReductions(year);
    } else {
      dispatch(notification({ type: "error", message: response?.data?.message || "Lưu thất bại" }));
    }
  };

  const removeReduction = async (row) => {
    if (!window.confirm(`Xoá giảm trừ của ${row.profileName} năm ${year}?`)) return;
    const { response, error } = await apiCall({
      ...API_URLS.RESEARCH_HOUR_CONFIG.deleteReduction(row.profileId, year),
    });
    if (!error && response?.data?.success) {
      dispatch(notification({ type: "success", message: "Đã xoá" }));
      loadReductions(year);
    } else {
      dispatch(notification({ type: "error", message: response?.data?.message || "Xoá thất bại" }));
    }
  };

  // -- Year close / reopen --------------------------------------------------
  const doCloseYear = async () => {
    setConfirmCloseOpen(false);
    const { response, error } = await apiCall({
      ...API_URLS.RESEARCH_HOUR_CONFIG.closeYear(),
      payload: { year },
    });
    if (!error && response?.data?.success) {
      dispatch(
        notification({
          type: "success",
          message: `Đã chốt năm ${year}: ${response.data.data?.count || 0} cán bộ`,
        }),
      );
      loadYearStatus(year);
    } else {
      dispatch(notification({ type: "error", message: response?.data?.message || "Chốt năm thất bại" }));
    }
  };

  const doReopenYear = async () => {
    setConfirmReopenOpen(false);
    const { response, error } = await apiCall({
      ...API_URLS.RESEARCH_HOUR_CONFIG.reopenYear(),
      payload: { year },
    });
    if (!error && response?.data?.success) {
      dispatch(notification({ type: "success", message: `Đã mở lại năm ${year}` }));
      loadYearStatus(year);
    } else {
      dispatch(notification({ type: "error", message: response?.data?.message || "Mở lại thất bại" }));
    }
  };

  // -- Profile dropdown options for creating reduction ---------------------
  const profileOptions = useMemo(() => {
    const taken = new Set(reductions.map((r) => r.profileId));
    return (allProfiles || []).filter((p) => !taken.has(p.id));
  }, [allProfiles, reductions]);

  return (
    <Box>
      {/* Year picker — applies to Section B and C only. */}
      <Grid container alignItems="center" spacing={2} style={{ marginBottom: 12 }}>
        <Grid item>
          <Typography variant="h5" style={{ fontWeight: 600 }}>
            Quota giờ nghiên cứu
          </Typography>
          <Typography variant="caption" style={{ color: "#666" }}>
            Cấu hình định mức (theo năm), giảm trừ (theo từng cán bộ), và chốt năm.
          </Typography>
        </Grid>
        <Grid item style={{ marginLeft: "auto" }}>
          <FormControl size="small" variant="outlined" style={{ minWidth: 120 }}>
            <InputLabel>Năm</InputLabel>
            <Select label="Năm" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10))}>
              {yearOptions.map((y) => (
                <MenuItem key={y} value={y}>
                  {y}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
      <Divider style={{ marginBottom: 16 }} />

      {/* Section C — banner with current year status */}
      <Paper variant="outlined" style={{ padding: 12, marginBottom: 20, backgroundColor: "#fafafa" }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item>
            {yearClosed ? (
              <Chip
                icon={<Lock fontSize="small" />}
                label={`Năm ${year} đã chốt`}
                style={{ backgroundColor: "#1976d2", color: "white", fontWeight: 600 }}
              />
            ) : (
              <Chip
                icon={<LockOpen fontSize="small" />}
                label={`Năm ${year} chưa chốt`}
                style={{ backgroundColor: "#f57c00", color: "white", fontWeight: 600 }}
              />
            )}
          </Grid>
          <Grid item style={{ flex: 1 }}>
            {yearClosed ? (
              <Typography variant="body2">
                Đã chốt {yearStatus?.count || 0} cán bộ • lúc{" "}
                {yearStatus?.closedAt ? new Date(yearStatus.closedAt).toLocaleString("vi-VN") : "—"}.
                Báo cáo năm này đọc từ bản chốt. Chỉnh định mức / giảm trừ sẽ bị chặn.
              </Typography>
            ) : (
              <Typography variant="body2">
                Báo cáo năm này đang được tính trực tiếp từ dữ liệu giờ NC hiện tại. Bấm "Chốt năm" khi sẵn sàng đóng dữ liệu.
              </Typography>
            )}
          </Grid>
          <Grid item>
            {yearClosed ? (
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<LockOpen />}
                onClick={() => setConfirmReopenOpen(true)}
              >
                Mở lại năm
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Lock />}
                onClick={() => setConfirmCloseOpen(true)}
              >
                Chốt năm {year}
              </Button>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Section A — Year quotas */}
      <Box style={{ marginBottom: 28 }}>
        <Grid container alignItems="center" style={{ marginBottom: 8 }}>
          <Grid item>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              A. Định mức chung theo năm
            </Typography>
            <Typography variant="caption" style={{ color: "#666" }}>
              Áp dụng cho tất cả cán bộ. Mặc định 586 giờ/năm. Năm chưa có sẽ tự tạo khi mở báo cáo lần đầu.
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: "auto" }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => setCreatingQuota({ year: currentYear + 1, hours: 586 })}
              style={{ marginRight: 8 }}
            >
              Thêm năm
            </Button>
            <Button variant="outlined" startIcon={<Replay />} onClick={loadQuotas}>
              Tải lại
            </Button>
          </Grid>
        </Grid>
        <KPTable
          shadow="none"
          loading={quotaLoading}
          rowsPerPage={20}
          maxHeight={300}
          columns={[
            {
              title: <b>Năm</b>,
              key: "year",
              width: "20%",
              sorter: (a, b) => a.year - b.year,
              render: (v) => <code style={{ fontWeight: 600 }}>{v}</code>,
            },
            {
              title: <b>Định mức (giờ)</b>,
              key: "hours",
              width: "30%",
              render: (v, row) =>
                edittingQuota?.year === row.year ? (
                  <TextField
                    autoFocus
                    size="small"
                    type="number"
                    variant="outlined"
                    value={edittingQuota.hours}
                    onChange={(e) => setEdittingQuota({ ...edittingQuota, hours: e.target.value })}
                    InputProps={{ endAdornment: <InputAdornment position="end">giờ</InputAdornment> }}
                  />
                ) : (
                  <span style={{ color: "#1976d2", fontWeight: 600 }}>{v} giờ</span>
                ),
            },
            {
              title: <b>Tác vụ</b>,
              key: "action",
              width: "30%",
              render: (_, row) =>
                edittingQuota?.year === row.year ? (
                  <>
                    <Typography
                      className="table-action-buton"
                      variant="caption"
                      color="primary"
                      onClick={() => saveQuota(edittingQuota)}
                    >
                      Lưu
                    </Typography>
                    <Typography
                      className="table-action-buton"
                      variant="caption"
                      color="secondary"
                      onClick={() => setEdittingQuota(null)}
                      style={{ marginLeft: 8 }}
                    >
                      Huỷ
                    </Typography>
                  </>
                ) : (
                  <Typography
                    className="table-action-buton"
                    variant="caption"
                    color="primary"
                    onClick={() => setEdittingQuota({ ...row })}
                  >
                    Sửa
                  </Typography>
                ),
            },
          ]}
          rows={quotas}
        />
      </Box>

      {/* Section B — Reductions */}
      <Box>
        <Grid container alignItems="center" style={{ marginBottom: 8 }}>
          <Grid item>
            <Typography variant="h6" style={{ fontWeight: 600 }}>
              B. Giảm trừ định mức theo người (năm {year})
            </Typography>
            <Typography variant="caption" style={{ color: "#666" }}>
              Mỗi cán bộ có thể có % giảm trừ riêng (vd: nghỉ thai sản 50%). Mặc định 0% nếu chưa cấu hình.
            </Typography>
          </Grid>
          <Grid item style={{ marginLeft: "auto" }}>
            <Tooltip title={yearClosed ? "Năm đã chốt, không thêm được" : ""}>
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Add />}
                  disabled={yearClosed}
                  onClick={() => setCreatingReduction({ profileId: null, pct: 0, note: "" })}
                  style={{ marginRight: 8 }}
                >
                  Thêm
                </Button>
              </span>
            </Tooltip>
            <Button variant="outlined" startIcon={<Replay />} onClick={() => loadReductions(year)}>
              Tải lại
            </Button>
          </Grid>
        </Grid>
        <KPTable
          shadow="none"
          loading={reductionLoading}
          rowsPerPage={20}
          maxHeight={400}
          columns={[
            { title: <b>Mã VC</b>, key: "profileCode", width: "12%", searchable: true },
            { title: <b>Họ tên</b>, key: "profileName", width: "22%", searchable: true },
            { title: <b>Khoa / Bộ môn</b>, key: "departmentName", width: "20%" },
            {
              title: <b>Giảm trừ (%)</b>,
              key: "pct",
              width: "15%",
              sorter: (a, b) => (a.pct || 0) - (b.pct || 0),
              render: (v, row) =>
                edittingReduction?.profileId === row.profileId ? (
                  <TextField
                    autoFocus
                    size="small"
                    type="number"
                    variant="outlined"
                    value={edittingReduction.pct}
                    onChange={(e) => setEdittingReduction({ ...edittingReduction, pct: e.target.value })}
                    InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  />
                ) : (
                  <span style={{ color: v > 0 ? "#d32f2f" : "#9e9e9e", fontWeight: 600 }}>{v}%</span>
                ),
            },
            {
              title: <b>Ghi chú</b>,
              key: "note",
              width: "16%",
              render: (v, row) =>
                edittingReduction?.profileId === row.profileId ? (
                  <TextField
                    size="small"
                    variant="outlined"
                    value={edittingReduction.note || ""}
                    onChange={(e) => setEdittingReduction({ ...edittingReduction, note: e.target.value })}
                  />
                ) : (
                  v || ""
                ),
            },
            {
              title: <b>Tác vụ</b>,
              key: "action",
              width: "15%",
              render: (_, row) =>
                edittingReduction?.profileId === row.profileId ? (
                  <>
                    <Typography
                      className="table-action-buton"
                      variant="caption"
                      color="primary"
                      onClick={() => saveReduction(edittingReduction)}
                    >
                      Lưu
                    </Typography>
                    <Typography
                      className="table-action-buton"
                      variant="caption"
                      color="secondary"
                      onClick={() => setEdittingReduction(null)}
                      style={{ marginLeft: 8 }}
                    >
                      Huỷ
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      className="table-action-buton"
                      variant="caption"
                      color="primary"
                      onClick={() => !yearClosed && setEdittingReduction({ ...row })}
                      style={{ pointerEvents: yearClosed ? "none" : "auto", opacity: yearClosed ? 0.4 : 1 }}
                    >
                      Sửa
                    </Typography>
                    <Typography
                      className="table-action-buton"
                      variant="caption"
                      color="secondary"
                      onClick={() => !yearClosed && removeReduction(row)}
                      style={{
                        marginLeft: 8,
                        pointerEvents: yearClosed ? "none" : "auto",
                        opacity: yearClosed ? 0.4 : 1,
                      }}
                    >
                      Xoá
                    </Typography>
                  </>
                ),
            },
          ]}
          rows={reductions}
        />
      </Box>

      {/* Create new quota dialog */}
      <Dialog open={!!creatingQuota} onClose={() => setCreatingQuota(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Thêm định mức năm</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            type="number"
            label="Năm"
            value={creatingQuota?.year || ""}
            onChange={(e) => setCreatingQuota({ ...creatingQuota, year: parseInt(e.target.value, 10) })}
          />
          <TextField
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            type="number"
            label="Số giờ định mức"
            value={creatingQuota?.hours ?? 586}
            onChange={(e) => setCreatingQuota({ ...creatingQuota, hours: e.target.value })}
            InputProps={{ endAdornment: <InputAdornment position="end">giờ</InputAdornment> }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatingQuota(null)}>Huỷ</Button>
          <Button onClick={() => saveQuota(creatingQuota)} color="primary" variant="contained" startIcon={<Save />}>
            Lưu
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create new reduction dialog */}
      <Dialog open={!!creatingReduction} onClose={() => setCreatingReduction(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Thêm giảm trừ năm {year}</DialogTitle>
        <DialogContent dividers>
          <Autocomplete
            options={profileOptions}
            getOptionLabel={(p) => `${p.code || "—"} • ${p.name}`}
            value={profileOptions.find((p) => p.id === creatingReduction?.profileId) || null}
            onChange={(_, p) => setCreatingReduction({ ...creatingReduction, profileId: p?.id || null })}
            renderInput={(params) => (
              <TextField {...params} size="small" variant="outlined" margin="dense" label="Cán bộ" />
            )}
          />
          <TextField
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            type="number"
            label="Giảm trừ (%)"
            value={creatingReduction?.pct ?? 0}
            onChange={(e) => setCreatingReduction({ ...creatingReduction, pct: e.target.value })}
            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          />
          <TextField
            fullWidth
            margin="dense"
            size="small"
            variant="outlined"
            label="Ghi chú (vd: nghỉ thai sản)"
            value={creatingReduction?.note || ""}
            onChange={(e) => setCreatingReduction({ ...creatingReduction, note: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatingReduction(null)}>Huỷ</Button>
          <Button
            onClick={() => saveReduction(creatingReduction)}
            color="primary"
            variant="contained"
            startIcon={<Save />}
          >
            Lưu
          </Button>
        </DialogActions>
      </Dialog>

      {/* Close year confirmation */}
      <Dialog open={confirmCloseOpen} onClose={() => setConfirmCloseOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Xác nhận chốt năm {year}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sau khi chốt, báo cáo năm {year} sẽ đọc từ bản chốt và bạn không thể sửa định mức / giảm trừ năm này
            (mở lại bằng "Mở lại năm"). Chốt nhiều lần đều an toàn — bản chốt sẽ được ghi đè.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmCloseOpen(false)}>Huỷ</Button>
          <Button onClick={doCloseYear} color="primary" variant="contained" startIcon={<Lock />}>
            Chốt năm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reopen year confirmation */}
      <Dialog open={confirmReopenOpen} onClose={() => setConfirmReopenOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Xác nhận mở lại năm {year}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bản chốt năm {year} sẽ bị xoá. Báo cáo quay lại tính trực tiếp từ dữ liệu hiện tại. Năm sau (nếu đã chốt)
            vẫn giữ nguyên giờ bù từ năm trước đã được lưu trong bản chốt.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmReopenOpen(false)}>Huỷ</Button>
          <Button onClick={doReopenYear} color="secondary" variant="contained" startIcon={<LockOpen />}>
            Mở lại năm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ResearchHourQuotaForm;
